import { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as IBAN from "iban";
import { Button } from "@accredo/fe-shared/theme/components";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { useBankAccountActions } from "hooks/useBankAccountActions.jsx";
import { useCustomerSearch } from "hooks/useCustomerSearch.js";
import { CustomerSelector } from "pages/users/components/CustomerSelector.jsx";

/**
 * Add a bank account by hand rather than via the OpenViva import.
 *
 * Skips the review step entirely (see BankAccountController::store) — someone
 * adding an account here has just declared it relevant, so requiring them to
 * then "review" their own entry would be pointless.
 *
 * The customer field only appears for accredo/sysadmin. A customer-scoped user
 * (kd_verwaltung) has exactly one valid answer, so making them search for and
 * pick their own customer would be busywork — the backend resolves it from
 * their session when kunde_id is omitted.
 */
export default function AddBankAccountDialog({ open, onClose, onCreated }) {
  const { user } = useUser();
  const { createAccount } = useBankAccountActions();
  const {
    customers,
    loading: searchLoading,
    searchCustomers,
    clearSearch,
  } = useCustomerSearch();

  const needsCustomerPicker = user?.hasPermission?.("vertrag_objekt_all");

  const emptyForm = {
    iban: "",
    bic: "",
    account_holder: "",
    bank_name: "",
    note: "",
  };

  const [form, setForm] = useState(emptyForm);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const setField = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  const ibanValue = form.iban.trim();
  const ibanIsValid = ibanValue === "" || IBAN.isValid(ibanValue);

  const isSubmitDisabled =
    !form.iban.trim() ||
    !ibanIsValid ||
    !form.bic.trim() ||
    !form.account_holder.trim() ||
    !form.bank_name.trim() ||
    (needsCustomerPicker && !selectedCustomer) ||
    isSaving;

  const handleClose = () => {
    setForm(emptyForm);
    setSelectedCustomer(null);
    clearSearch();
    onClose();
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    const result = await createAccount({
      ...form,
      iban: ibanValue,
      // Omitted (not sent as null/undefined) for a customer-scoped user, so
      // the backend's own-customer resolution applies.
      ...(needsCustomerPicker && { kunde_id: selectedCustomer.id }),
    });

    setIsSaving(false);

    if (result) {
      onCreated?.();
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Neues Bankkonto</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {needsCustomerPicker && (
            <CustomerSelector
              customers={customers}
              selected_customer={selectedCustomer}
              on_customer_change={(event, value) => setSelectedCustomer(value)}
              on_customer_input_change={(event, value) => {
                if (value.length >= 2) {
                  searchCustomers(value);
                } else {
                  clearSearch();
                }
              }}
              search_loading={searchLoading}
              message="Für welchen Kunden gilt dieses Konto?"
            />
          )}

          <TextField
            label="IBAN"
            value={form.iban}
            onChange={setField("iban")}
            required
            autoFocus
            error={!ibanIsValid}
            helperText={!ibanIsValid ? "Ungültige IBAN" : " "}
          />

          <TextField
            label="BIC"
            value={form.bic}
            onChange={setField("bic")}
            required
          />

          <TextField
            label="Kontoinhaber"
            value={form.account_holder}
            onChange={setField("account_holder")}
            required
          />

          <TextField
            label="Bank"
            value={form.bank_name}
            onChange={setField("bank_name")}
            required
          />

          <TextField
            label="Notiz"
            value={form.note}
            onChange={setField("note")}
            multiline
            minRows={2}
          />

          <Typography variant="body2" color="text.secondary">
            Das Konto ist nach dem Anlegen sofort für Schadenzahlungen
            hinterlegt.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="tertiary" onClick={handleClose}>
          Abbrechen
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          Anlegen
        </Button>
      </DialogActions>
    </Dialog>
  );
}
